import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import ModalCloseButton from '../modals/ModalCloseButton';
import './DeviceReliabilityHelpModal.css';

/**
 * DeviceReliabilityHelpModal — explains reliability metrics on Device view.
 * Opened from Device → Reliability Metrics & Analytics help (?).
 * Styled to match AlertHelpModal: clean sections, tables, and key info lists.
 */
function DeviceReliabilityHelpModal({ isOpen, onClose }) {
  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const content = (
    <div className="reliability-help-overlay" onClick={onClose}>
      <div className="reliability-help-modal" onClick={(e) => e.stopPropagation()}>
        <div className="reliability-help-header">
          <h2>Device Reliability Metrics</h2>
          <ModalCloseButton onClick={onClose} title="Close" />
        </div>

        <div className="reliability-help-body">
          <section className="reliability-help-section">
            <h3>Metric Definitions</h3>
            <p className="reliability-help-intro">
              Most metrics use a <strong>rolling last 12 months</strong> window
              (from device installation if newer). <strong>Current downtime</strong> and{' '}
              <strong>device health</strong> also reflect <strong>live</strong> active outages.
            </p>
            <table className="reliability-help-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Definition</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Availability %</strong></td>
                  <td>
                    Uptime in the 12-month window minus weighted downtime from complaints that impact availability.
                    Minor complaints count only after a technician confirms availability impact at In Progress.
                  </td>
                </tr>
                <tr>
                  <td><strong>Current downtime</strong></td>
                  <td>
                    Live: ongoing impacting complaints from breakdown date until now.
                  </td>
                </tr>
                <tr>
                  <td><strong>Resolved downtime</strong></td>
                  <td>
                    Closed impacting complaints in the 12-month window.
                  </td>
                </tr>
                <tr>
                  <td><strong>Avg restore time</strong></td>
                  <td>
                    Average breakdown-to-closed duration for closed impacting complaints (12 mo).
                  </td>
                </tr>
                <tr>
                  <td><strong>MTBF</strong></td>
                  <td>
                    Average time between impacting failure start dates (needs at least two failures in the window).
                  </td>
                </tr>
                <tr>
                  <td><strong>Device health</strong></td>
                  <td>
                    Critical / At risk / Healthy based on live critical complaints, current downtime,
                    12-month availability, and maintenance overdue status.
                  </td>
                </tr>
                <tr>
                  <td><strong>Minor complaints</strong></td>
                  <td>
                    When a technician scans the device and starts work, they confirm whether a minor issue
                    affects clinical use. That choice is locked after work begins.
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="reliability-help-section">
            <h3>Key Information</h3>
            <ul>
              <li><strong>12-month window:</strong> Most metrics roll over the last 12 months, or since installation if the device is newer</li>
              <li><strong>Live vs historical:</strong> Current downtime and device health include active outages right now</li>
              <li><strong>Availability impact:</strong> Minor complaints only affect availability after technician confirmation at In Progress</li>
              <li><strong>MTBF requirement:</strong> Needs at least two impacting failures in the window to calculate</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}

export default DeviceReliabilityHelpModal;
